import React, { useContext, useState, useRef, useEffect } from 'react';
import {
  Menu,
  X,
  ArrowLeft,
  Copy,
  FileDown,
  History,
  Loader,
  UserRoundPlus,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BlogContext } from 'features/ai-blog-writer/BlogContext';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import AddNewUser from '../Modals/AddNewUser';
import BlogSaveAs from '../Modals/blogSaveAs';
import toast from 'react-hot-toast';
import CheckAuthModel from '../Modals/CheckAuthModel';

interface BlogHeaderProps {
  onEditClick: () => void;
  isEditPanelOpen?: boolean;
  isHistoryPanelOpen?: boolean;
  onHisotryPanelClick: () => void;
}

export function BlogHeader({
  onEditClick,
  isEditPanelOpen,
  isHistoryPanelOpen,
  onHisotryPanelClick,
}: BlogHeaderProps) {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { blogContent, cleanUp, isGenerating } = useContext(BlogContext);
  const [isExporting, setIsExporting] = useState('Export');
  const [searchParams] = useSearchParams();
  const blogId = searchParams.get('id');
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(String(blogContent?.markDownContent))
      .finally(() => toast.success('Content copied to clipboard'));
  };

  function showLoadingCursor() {
    document.body.classList.add('loading-cursor');
  }

  function hideLoadingCursor() {
    document.body.classList.remove('loading-cursor');
  }

  const handleExport = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    showLoadingCursor();
    setIsExporting('Exporting...');
    try {
      const contentDiv = document.querySelector('.ql-editor');
      if (!contentDiv) {
        throw new Error(`Content div with selector .ql-editor not found`);
      }

      const printStyles = `
        <style>
          @media print {
            @page {
              size: A4;
              margin: 20mm 15mm;
            }
            
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            h1 {
              font-size: 24pt;
              margin-bottom: 15pt;
              page-break-after: avoid;
            }
            
            h2 {
              font-size: 20pt;
              margin-bottom: 12pt;
              page-break-after: avoid;
            }
            
            h3 {
              font-size: 16pt;
              margin-top: 20pt;
              margin-bottom: 10pt;
              page-break-after: avoid;
            }
            
            p {
              font-size: 12pt;
              line-height: 1.6;
              margin-bottom: 10pt;
              text-align: justify;
            }
            
            img {
              max-width: 180mm;
              height: auto;
              margin: 10pt auto;
              display: block;
              page-break-inside: avoid;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15pt 0;
              page-break-inside: avoid;
            }
            
            th, td {
              padding: 8pt;
              border: 1pt solid #ddd;
            }
            
            ul, ol {
              margin: 10pt 0;
              padding-left: 20pt;
            }
            
            li {
              margin-bottom: 5pt;
            }
            
            .page-break {
              page-break-before: always;
            }
            
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
            }
            
            img, table, figure {
              page-break-inside: avoid;
            }
          }
        </style>
        <style>
          * {
            font-family: Arial, system-ui, -apple-system, sans-serif;
            text-rendering: optimizeLegibility;
            -webkit-font-smoothing: antialiased;
          }
          
          .print-container {
            width: 210mm;
            margin: 0 auto;
            padding: 20mm 15mm;
            font-family: Arial, system-ui, -apple-system, sans-serif;
          }
        </style>
      `;

      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Preview</title>
            ${printStyles}
          </head>
          <body>
            <div class="print-container">
              ${contentDiv.innerHTML}
            </div>
            <script>
              window.onload = () => {
                setTimeout(() => {
                  window.print();
                }, 500);
              };
            </script>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow?.document.write(printContent);
      printWindow?.document.close();
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Export failed');
    } finally {
      hideLoadingCursor();
      setIsExporting('Export');
    }
  };

  const handleBack = () => {
    navigate(-1);
    cleanUp();
  };

  const handleEditClick = () => {
    if (isAuthenticated) {
      onEditClick();
    } else {
      setShowAuthModal(true);
    }
  };

  const handleHistoryPanelClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!blogId) {
      toast.error('Save your blog to access history');
      return;
    }

    onHisotryPanelClick();
  };

  return (
    <header className="border-b rounded-t-lg relative">
      <div className="px-4">
        <div className="h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 text-text-primary w-full">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              disabled={isGenerating}
            >
              <ArrowLeft className="h-4 w-4 text-primary" />
            </Button>
            <div className="min-w-0 flex-1 max-w-[240px] sm:max-w-[260px] md:max-w-[400px] lg:max-w-[600px] xl:max-w-[800px]">
              <h1 className="font-semibold text-[14px] lg:text-xl truncate">
                Blog Writer {blogContent?.title ? `/ ${blogContent.title}` : ''}
              </h1>
            </div>
          </div>

          {/* Desktop Menu */}
          {isEditPanelOpen || isHistoryPanelOpen ? (
            <></>
          ) : (
            <div className={`hidden md:flex items-center gap-4 text-primary`}>
              {/* Version History */}
              {blogContent?.settings?.permission != 1 && (
                <Button
                  variant="customSecondary"
                  className="text-primary p-3 bg-background hover:bg-background hover:text-primary"
                  size="icon"
                  onClick={handleHistoryPanelClick}
                  disabled={isGenerating}
                >
                  <History className="h-4 w-4" />
                </Button>
              )}

              {/* Copy */}
              <Button
                variant="customSecondary"
                className="text-primary p-3 bg-background hover:bg-background hover:text-primary"
                size="icon"
                onClick={handleCopy}
                disabled={isGenerating}
              >
                <Copy className="h-4 w-4" />
              </Button>

              {/* Collaboration */}
              <div className="relative" ref={dropdownRef}>
                <Button
                  onClick={() => {
                    if (!isAuthenticated) {
                      setShowAuthModal(true);
                    } else if (!blogId) {
                      toast.error('Save your blog to access collaboration');
                    } else {
                      setShowDropdown(!showDropdown);
                    }
                  }}
                  variant="customSecondary"
                  className="text-primary bg-secondary"
                  disabled={isGenerating}
                >
                  <UserRoundPlus className="h-4 w-4" />
                </Button>

                {showDropdown && isAuthenticated && blogId && (
                  <div className="absolute right-[400px] top-full mt-2 z-50">
                    <AddNewUser isOpen={showDropdown} />
                  </div>
                )}
              </div>

              {/* Export */}
              <Button
                variant="customSecondary"
                className="w-auto text-[14px] text-primary justify-center flex items-center bg-secondary"
                onClick={handleExport}
                disabled={isGenerating || isExporting !== 'Export'}
              >
                {isExporting}
                {isExporting === 'Export' ? (
                  <FileDown className="w-4 h-4 ml-2" />
                ) : (
                  <Loader className="w-4 h-4 ml-2" />
                )}
              </Button>

              {/* Edit */}
              {blogContent?.settings?.permission != 1 && (
                <Button
                  variant="customSecondary"
                  onClick={handleEditClick}
                  className="text-[14px] w-auto text-primary justify-center flex items-center bg-secondary"
                  disabled={isGenerating}
                >
                  Edit
                </Button>
              )}

              {/* BlogSaveAs component */}
              {!isGenerating && <BlogSaveAs />}
            </div>
          )}

          {/* Mobile Hamburger Button */}
          {!isEditPanelOpen && !isHistoryPanelOpen && (
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              disabled={isGenerating}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6 text-primary" />
              ) : (
                <Menu className="h-6 w-6 text-primary" />
              )}
            </Button>
          )}
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && !isEditPanelOpen && !isHistoryPanelOpen && (
          <div className="absolute left-0 right-0 top-[56px] bg-background border-t shadow-lg z-50 md:hidden">
            <div className="py-4 px-4 space-y-2">
              {/* Mobile Version History */}
              <Button
                variant="customSecondary"
                className="text-primary bg-secondary w-full justify-start"
                onClick={handleHistoryPanelClick}
                disabled={isGenerating}
              >
                <History className="h-4 w-4 mr-2" />
                Version History
              </Button>

              <div className="relative w-full">
                <Button
                  onClick={() => {
                    if (!isAuthenticated) {
                      setShowAuthModal(true);
                    } else if (!blogId) {
                      toast.error('Save your blog to access collaboration');
                    } else {
                      setShowDropdown(!showDropdown);
                    }
                  }}
                  variant="customSecondary"
                  className="text-primary bg-secondary w-full justify-start"
                  disabled={isGenerating}
                >
                  <UserRoundPlus className="h-4 w-4 mr-2" />
                  Collaborate
                </Button>
                {showDropdown && isAuthenticated && blogId && (
                  <div className="absolute left-0 right-0 mt-2 z-50">
                    <AddNewUser isOpen={showDropdown} />
                  </div>
                )}
              </div>

              <Button
                variant="customSecondary"
                className="text-primary bg-secondary w-full justify-start"
                onClick={handleExport}
                disabled={isGenerating || isExporting !== 'Export'}
              >
                {isExporting}
                {isExporting === 'Export' ? (
                  <FileDown className="h-4 w-4 ml-2" />
                ) : (
                  <Loader className="h-4 w-4 ml-2" />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Add CheckAuthModel */}
        {showAuthModal && (
          <CheckAuthModel setVisibleEditModel={setShowAuthModal} />
        )}
      </div>
    </header>
  );
}

export default BlogHeader;
